import { Button, Col, Icon, Input, Row, Sidepanel, Switch, Text } from 'components';
import { Api, theme } from 'lib';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ContentHeader, IrreversableModal } from 'screens/Admin/components';

const DateInput = styled.input`
  width: 100%;
  height: 36px;
  border-radius: 6px;
  border: 1px solid ${(props) => (props.hasError ? theme.RED : theme.GREY_30)};
  padding: 0 10px;
  font-family: CeraPro;
  font-size: 13px;
  color: ${theme.GREY_80};
  background: white;
`;

const Banner = styled.div`
  background: ${(props) => (props.tone === 'error' ? `${theme.RED}10` : `${theme.GREY_20}`)};
  border: 1px solid ${(props) => (props.tone === 'error' ? theme.RED : theme.GREY_30)};
  color: ${(props) => (props.tone === 'error' ? theme.RED : theme.GREY_80)};
  border-radius: 8px;
  padding: 10px 14px;
  font-family: CeraPro;
  font-size: 13px;
  margin-bottom: 12px;
`;

const RowCell = styled.div`
  flex: ${(props) => props.flex || 1};
  padding: 0 6px;
`;

const RowWrap = styled.div`
  border: 1px solid ${(props) => (props.bad ? theme.RED : theme.GREY_20)};
  background: ${(props) => (props.bad ? `${theme.RED}08` : 'white')};
  border-radius: 8px;
  padding: 8px 6px;
  margin-bottom: 6px;
`;

const ExpandToggle = styled.button`
  background: none;
  border: 1px solid ${theme.GREY_30};
  border-radius: 6px;
  padding: 4px 10px;
  font-family: CeraPro;
  font-size: 12px;
  color: ${theme.GREY_80};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover {
    border-color: ${theme.GREY_60};
  }
`;

// Compact segmented control for the content language (English / 日本語) — matches QuizForm.
const LangPill = styled.button`
  height: 30px;
  padding: 0 14px;
  border: 1px solid ${(props) => (props.active ? theme.SECONDARY : theme.GREY_30)};
  background: ${(props) => (props.active ? `${theme.SECONDARY}15` : 'white')};
  color: ${(props) => (props.active ? theme.SECONDARY : theme.GREY_60)};
  font-family: CeraPro;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:first-child {
    border-radius: 8px 0 0 8px;
  }
  &:last-child {
    border-radius: 0 8px 8px 0;
    border-left: none;
  }
`;

const COUNT_PRESETS = [
  { label: '7 days', value: 7 },
  { label: '15 days', value: 15 },
  { label: '30 days', value: 30 }
];

const pad = (n) => String(n).padStart(2, '0');

const toDatetimeLocal = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;

const fromDatetimeLocal = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const cloneAnswers = (answers) =>
  answers.map((a) => ({ text: a.text, textJa: a.textJa || '', isCorrect: !!a.isCorrect }));

// Indices of items whose `getText` value duplicates an earlier one — trimmed,
// case-insensitive. Empty values are ignored. Drives the live red-border + warning.
const duplicateIndices = (items, getText) => {
  const seen = new Map();
  const dupes = new Set();
  items.forEach((it, i) => {
    const key = (getText(it) || '').trim().toLowerCase();
    if (!key) return;
    if (seen.has(key)) {
      dupes.add(i);
      dupes.add(seen.get(key));
    } else {
      seen.set(key, i);
    }
  });
  return dupes;
};

// Build N row objects starting from `baseDate` at `startHHMM`, ending at `endHHMM` each day.
const generateRows = ({
  count,
  baseDate,
  startTime,
  endTime,
  rewardPoints,
  isActive,
  questionTemplate,
  questionTemplateJa,
  answerTemplate
}) => {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const rows = [];
  const base = new Date(baseDate);
  for (let i = 0; i < count; i++) {
    const start = new Date(base);
    start.setDate(base.getDate() + i);
    start.setHours(sh, sm, 0, 0);
    const end = new Date(base);
    end.setDate(base.getDate() + i);
    end.setHours(eh, em, 0, 0);
    if (end <= start) end.setDate(end.getDate() + 1);
    const question = (questionTemplate || 'Daily quiz {{n}}')
      .replace(/\{\{i\}\}/g, String(i + 1))
      .replace(/\{\{n\}\}/g, String(i + 1));
    // Japanese is optional — an empty template just leaves the rows untranslated.
    const questionJa = (questionTemplateJa || '')
      .replace(/\{\{i\}\}/g, String(i + 1))
      .replace(/\{\{n\}\}/g, String(i + 1));
    rows.push({
      question,
      questionJa,
      rewardPoints: Number(rewardPoints) || 0,
      isActive,
      startDate: toDatetimeLocal(start),
      endDate: toDatetimeLocal(end),
      answers: cloneAnswers(answerTemplate)
    });
  }
  return rows;
};

const BulkCreateForm = ({
  visible = true,
  teamId,
  existingQuizzes = [],
  onClose,
  onSaved
}) => {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const [count, setCount] = useState(7);
  const [customCount, setCustomCount] = useState('');
  const [baseDate, setBaseDate] = useState(todayStr);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('21:00');
  const [questionTemplate, setQuestionTemplate] = useState('Daily quiz {{n}}');
  const [questionTemplateJa, setQuestionTemplateJa] = useState('');
  const [defaultPoints, setDefaultPoints] = useState('10');
  const [defaultActive, setDefaultActive] = useState(true);
  // Multi-language content: Japanese question/answers are optional — empty fields fall
  // back to English in the fan app. Mirrors the single-create QuizForm toggle.
  const [contentLang, setContentLang] = useState('en');

  const [answerTemplate, setAnswerTemplate] = useState([
    { text: '', textJa: '', isCorrect: true },
    { text: '', textJa: '', isCorrect: false }
  ]);

  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({}); // { [rowIndex]: true }
  const [topError, setTopError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmReapplyOpen, setConfirmReapplyOpen] = useState(false);
  // Snapshot of the team's active daily quizzes used for proactive conflict detection.
  // Refreshed on panel open and after the server reports an unexpected 409.
  const [existingActive, setExistingActive] = useState(existingQuizzes);

  // Reset every field whenever the panel reopens so each session starts clean,
  // while keeping the panel mounted (lets Sidepanel's slide animation run).
  useEffect(() => {
    if (!visible) return;
    setCount(7);
    setCustomCount('');
    setBaseDate(todayStr);
    setStartTime('09:00');
    setEndTime('21:00');
    setQuestionTemplate('Daily quiz {{n}}');
    setQuestionTemplateJa('');
    setDefaultPoints('10');
    setDefaultActive(true);
    setContentLang('en');
    setAnswerTemplate([
      { text: '', textJa: '', isCorrect: true },
      { text: '', textJa: '', isCorrect: false }
    ]);
    setRows([]);
    setExpanded({});
    setTopError(null);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Pull every active daily quiz on this team. Server caps page size at 100, so we
  // paginate. Silent — a background prefetch should never spawn a toast.
  const fetchAllActiveDaily = async () => {
    const PAGE_SIZE = 100;
    const all = [];
    for (let offset = 0; offset < 1000; offset += PAGE_SIZE) {
      const res = await Api.get(
        `/admin/quizzes/list?teamId=${teamId}&limit=${PAGE_SIZE}&offset=${offset}&isActive=true`,
        null,
        { silent: true }
      );
      if (!res?.data) break;
      all.push(...res.data);
      if (res.data.length < PAGE_SIZE) break;
      if (res.pagination?.total && all.length >= res.pagination.total) break;
    }
    return all.filter((q) => q.quizType === 'daily' && q.startDate && q.endDate);
  };

  // Fetch the team's active daily quizzes once per open so we can show conflicts
  // proactively (server only reports the first conflict; we want to mark all of them).
  useEffect(() => {
    if (!visible || !teamId) return;
    let cancelled = false;
    (async () => {
      const daily = await fetchAllActiveDaily();
      if (!cancelled) setExistingActive(daily);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, teamId]);

  // Per-row conflict map. For each row index, records the FIRST conflict found:
  //   { kind: 'existing', quiz }           - overlaps a quiz already on the team
  //   { kind: 'batch', otherIndex: number } - overlaps another row in this same batch
  // Recomputes whenever rows or the existing snapshot change, so edits clear/refresh
  // the flags in real time.
  const rowConflicts = useMemo(() => {
    const result = {};
    if (!rows.length) return result;

    const rowWindows = rows.map((r) => {
      if (!r.startDate || !r.endDate) return null;
      const s = new Date(r.startDate).getTime();
      const e = new Date(r.endDate).getTime();
      if (!(e > s)) return null;
      return { s, e };
    });

    const existingWindows = existingActive
      .map((q) => ({
        quiz: q,
        s: new Date(q.startDate).getTime(),
        e: new Date(q.endDate).getTime()
      }))
      .filter((w) => w.e > w.s);

    rowWindows.forEach((w, i) => {
      if (!w) return;
      const hitExisting = existingWindows.find((ew) => w.s < ew.e && w.e > ew.s);
      if (hitExisting) {
        result[i] = { kind: 'existing', quiz: hitExisting.quiz };
        return;
      }
      for (let j = 0; j < rowWindows.length; j++) {
        if (j === i) continue;
        const other = rowWindows[j];
        if (!other) continue;
        if (w.s < other.e && w.e > other.s) {
          result[i] = { kind: 'batch', otherIndex: j };
          return;
        }
      }
    });
    return result;
  }, [rows, existingActive]);

  const conflictCount = Object.keys(rowConflicts).length;
  const hasAnyConflict = conflictCount > 0;

  const [shifting, setShifting] = useState(false);

  // Recompute every row's schedule to skip days that overlap an existing active daily
  // quiz on this channel OR an already-placed row in the batch. Starts from the
  // earliest planned baseDate and walks forward one day at a time. Daily cadence
  // (1 day apart, original start/end times) is preserved; conflicting days are skipped.
  // Uses the cached `existingActive` snapshot — fetched on panel open — so the shift
  // is synchronous and instant.
  const handleAutoShift = () => {
    if (!rows.length) return;
    setShifting(true);
    try {
      const existingWindows = existingActive
        .map((q) => ({
          start: new Date(q.startDate).getTime(),
          end: new Date(q.endDate).getTime()
        }))
        .filter((w) => w.end > w.start);

      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);

      // Anchor on the row whose original start is earliest (handles edited rows too).
      const earliestStart = rows
        .map((r) => new Date(r.startDate).getTime())
        .reduce((a, b) => Math.min(a, b), Infinity);
      const cursor = new Date(earliestStart);
      cursor.setHours(0, 0, 0, 0);

      const placedWindows = [];
      const placedRows = [];

      // Safety bound: never walk further than 5 years past the start.
      const maxDays = 365 * 5;
      let daysWalked = 0;

      while (placedRows.length < rows.length && daysWalked < maxDays) {
        const start = new Date(cursor);
        start.setHours(sh, sm, 0, 0);
        const end = new Date(cursor);
        end.setHours(eh, em, 0, 0);
        if (end.getTime() <= start.getTime()) end.setDate(end.getDate() + 1);

        const s = start.getTime();
        const e = end.getTime();
        const overlaps =
          existingWindows.some((w) => s < w.end && e > w.start) ||
          placedWindows.some((w) => s < w.end && e > w.start);

        if (!overlaps) {
          placedWindows.push({ start: s, end: e });
          const idx = placedRows.length;
          placedRows.push({
            ...rows[idx],
            startDate: toDatetimeLocal(start),
            endDate: toDatetimeLocal(end)
          });
        }
        cursor.setDate(cursor.getDate() + 1);
        daysWalked += 1;
      }

      if (placedRows.length < rows.length) {
        setTopError(
          `Couldn't find ${rows.length} free days within 5 years of the base date. ` +
            `Try shorter batches or change the daily window.`
        );
        return;
      }

      setRows(placedRows);
      setTopError(null);
    } finally {
      setShifting(false);
    }
  };

  const effectiveCount = customCount ? Math.max(1, Math.min(60, Number(customCount))) : count;

  const handleGenerate = () => {
    setTopError(null);
    setExpanded({});
    const r = generateRows({
      count: effectiveCount,
      baseDate,
      startTime,
      endTime,
      rewardPoints: defaultPoints,
      isActive: defaultActive,
      questionTemplate,
      questionTemplateJa,
      answerTemplate
    });
    setRows(r);
  };

  const reapplyTemplateToAll = () => {
    if (!rows.length) return;
    setConfirmReapplyOpen(true);
  };

  const doReapplyTemplate = () => {
    setRows((prev) => prev.map((r) => ({ ...r, answers: cloneAnswers(answerTemplate) })));
    setConfirmReapplyOpen(false);
  };

  const updateRow = (i, patch) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    // rowConflicts recomputes via useMemo — no manual clearing needed.
    setTopError(null);
  };

  const removeRow = (i) => {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
    setExpanded((prev) => {
      const next = { ...prev };
      delete next[i];
      return next;
    });
    setTopError(null);
  };

  const toggleExpand = (i) => {
    setExpanded((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  // ---- Template answer editor ----
  const updateTemplateAnswerText = (i, value) => {
    setAnswerTemplate((prev) => prev.map((a, idx) => (idx === i ? { ...a, text: value } : a)));
  };
  const updateTemplateAnswerTextJa = (i, value) => {
    setAnswerTemplate((prev) => prev.map((a, idx) => (idx === i ? { ...a, textJa: value } : a)));
  };
  const updateTemplateAnswerCorrect = (i) => {
    setAnswerTemplate((prev) => prev.map((a, idx) => ({ ...a, isCorrect: idx === i })));
  };
  const addTemplateAnswer = () => {
    if (answerTemplate.length >= 10) return;
    setAnswerTemplate((prev) => [...prev, { text: '', textJa: '', isCorrect: false }]);
  };
  const removeTemplateAnswer = (i) => {
    if (answerTemplate.length <= 2) return;
    setAnswerTemplate((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      if (!next.some((a) => a.isCorrect)) next[0].isCorrect = true;
      return next;
    });
  };

  // ---- Per-row answer editor ----
  const updateRowAnswerText = (rowIdx, ansIdx, value) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx
          ? {
              ...r,
              answers: r.answers.map((a, j) => (j === ansIdx ? { ...a, text: value } : a))
            }
          : r
      )
    );
  };
  const updateRowAnswerTextJa = (rowIdx, ansIdx, value) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx
          ? {
              ...r,
              answers: r.answers.map((a, j) => (j === ansIdx ? { ...a, textJa: value } : a))
            }
          : r
      )
    );
  };
  const updateRowAnswerCorrect = (rowIdx, ansIdx) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx
          ? { ...r, answers: r.answers.map((a, j) => ({ ...a, isCorrect: j === ansIdx })) }
          : r
      )
    );
  };
  const addRowAnswer = (rowIdx) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx && r.answers.length < 10
          ? { ...r, answers: [...r.answers, { text: '', textJa: '', isCorrect: false }] }
          : r
      )
    );
  };
  const removeRowAnswer = (rowIdx, ansIdx) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== rowIdx || r.answers.length <= 2) return r;
        const nextAnswers = r.answers.filter((_, j) => j !== ansIdx);
        if (!nextAnswers.some((a) => a.isCorrect)) nextAnswers[0].isCorrect = true;
        return { ...r, answers: nextAnswers };
      })
    );
  };

  const validate = () => {
    if (!rows.length) return 'Generate rows before submitting';
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.question.trim()) return `Row ${i + 1}: question is required`;
      if (r.rewardPoints == null || Number(r.rewardPoints) < 0)
        return `Row ${i + 1}: reward points must be 0 or greater`;
      if (!r.startDate || !r.endDate) return `Row ${i + 1}: schedule is required`;
      if (new Date(r.endDate) <= new Date(r.startDate))
        return `Row ${i + 1}: end must be after start`;

      const trimmed = r.answers
        .map((a) => ({ ...a, text: a.text.trim() }))
        .filter((a) => a.text.length > 0);
      if (trimmed.length < 2) return `Row ${i + 1}: needs at least 2 answers`;
      if (!trimmed.some((a) => a.isCorrect))
        return `Row ${i + 1}: pick a correct answer`;
      const norm = (s) => s.trim().toLowerCase();
      if (new Set(trimmed.map((a) => norm(a.text))).size !== trimmed.length)
        return `Row ${i + 1}: duplicate options are not allowed. Please enter a unique option.`;
      const jaTexts = trimmed.map((a) => a.textJa).filter((t) => t && t.trim());
      if (new Set(jaTexts.map(norm)).size !== jaTexts.length)
        return `Row ${i + 1}: duplicate Japanese options are not allowed. Please enter a unique option.`;
    }
    return null;
  };

  const refreshExistingActive = async () => {
    const daily = await fetchAllActiveDaily();
    setExistingActive(daily);
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setTopError(err);
      const m = /^Row (\d+):/.exec(err);
      if (m) setExpanded((prev) => ({ ...prev, [Number(m[1]) - 1]: true }));
      return;
    }

    // Pre-emptive: don't even call the API if we already know rows conflict.
    if (hasAnyConflict) {
      setTopError(
        `${conflictCount} row${conflictCount === 1 ? '' : 's'} conflict — use Auto-shift or edit the highlighted dates.`
      );
      return;
    }

    setTopError(null);
    setSubmitting(true);

    const payload = {
      teamId,
      quizzes: rows.map((r) => ({
        question: r.question.trim(),
        questionJa: (r.questionJa || '').trim() || undefined,
        answers: r.answers
          .filter((a) => a.text.trim().length > 0)
          .map((a) => {
            const ans = { text: a.text.trim(), isCorrect: !!a.isCorrect };
            if (a.textJa && a.textJa.trim()) ans.textJa = a.textJa.trim();
            return ans;
          }),
        rewardPoints: Number(r.rewardPoints),
        isActive: !!r.isActive,
        startDate: fromDatetimeLocal(r.startDate),
        endDate: fromDatetimeLocal(r.endDate)
      }))
    };

    const res = await Api.post('/admin/quizzes/bulkCreate', payload, null, { silent: true });
    setSubmitting(false);

    if (res && res.err && res.status) {
      if (res.status === 409) {
        // Server saw a conflict the client hadn't — most likely the team's quiz set
        // changed since we opened. Pull a fresh snapshot so rowConflicts catches up.
        setTopError(res.err);
        refreshExistingActive();
        return;
      }
      setTopError(res.err);
      return;
    }

    if (res?.data?.count) {
      onSaved(res.data.count);
      onClose();
    }
  };

  // Japanese translation progress. Once rows exist they are the source of truth;
  // before generating, count the template slots instead.
  const isJa = contentLang === 'ja';
  const jaSlots = rows.length
    ? rows.flatMap((r) => [
        r.questionJa,
        ...r.answers.filter((a) => a.text && a.text.trim()).map((a) => a.textJa)
      ])
    : [
        questionTemplateJa,
        ...answerTemplate.filter((a) => a.text && a.text.trim()).map((a) => a.textJa)
      ];
  const jaDone = jaSlots.filter((v) => v && v.trim()).length;
  const jaTotal = jaSlots.length;

  // Duplicate detection for the default answer template, in the currently-viewed language.
  const dupTemplateIdx = duplicateIndices(answerTemplate, (a) => (isJa ? a.textJa : a.text));

  return (
    <Sidepanel visible={visible} wid={1100} pad="0" closeModal={() => {}}>
      <ContentHeader text="Bulk pre-create daily quizzes" closePanel={onClose} />
      <Col wid="100%" pad="20px 24px">
        {/* Content language — mirrors the single-create QuizForm toggle. */}
        <Row between center noFlex marg="0 0 12px" gap="12px">
          <Col noFlex style={{ minWidth: 0 }}>
            <Text h4 marg="0">
              Content language
            </Text>
            <Text c2 col={theme.GREY_60} marg="2px 0 0">
              Japanese is optional — empty fields fall back to English in the fan app.
            </Text>
          </Col>
          <Row noFlex center>
            <LangPill type="button" active={!isJa} onClick={() => setContentLang('en')}>
              English
            </LangPill>
            <LangPill type="button" active={isJa} onClick={() => setContentLang('ja')}>
              日本語 · {jaDone}/{jaTotal}
            </LangPill>
          </Row>
        </Row>

        {isJa && (
          <Banner>
            Translating to Japanese. Counts, schedule, answers and the correct answer are
            managed on the English tab — here you only translate the text. English text is
            shown as the placeholder. The Japanese question template is applied when rows
            are generated.
          </Banner>
        )}

        {!isJa && (
          <Text h4 marg="0 0 8px">
            1. Generate
          </Text>
        )}

        {!isJa && (
        <Row gap="12px" marg="0 0 12px">
          {COUNT_PRESETS.map((p) => (
            <Button
              key={p.value}
              text={p.label}
              onClick={() => {
                setCount(p.value);
                setCustomCount('');
              }}
              bordered={!(count === p.value && !customCount)}
              col={count === p.value && !customCount ? theme.PRIMARY : undefined}
              style={{ width: 'fit-content', padding: '6px 14px' }}
            />
          ))}
          <Col noFlex wid="160px">
            <Input
              noTitle
              type="number"
              placeholder="Custom (1–60)"
              value={customCount}
              onChange={(e) => setCustomCount(e.target.value)}
            />
          </Col>
        </Row>
        )}

        {!isJa && (
        <Row gap="12px" marg="0 0 12px">
          <Col flex={1}>
            <Text h5 marg="0 0 4px">
              Base date
            </Text>
            <DateInput
              type="date"
              value={baseDate}
              onChange={(e) => setBaseDate(e.target.value)}
            />
          </Col>
          <Col flex={1}>
            <Text h5 marg="0 0 4px">
              Daily start
            </Text>
            <DateInput
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </Col>
          <Col flex={1}>
            <Text h5 marg="0 0 4px">
              Daily end
            </Text>
            <DateInput type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </Col>
          <Col flex={1}>
            <Text h5 marg="0 0 4px">
              Default reward
            </Text>
            <DateInput
              type="number"
              value={defaultPoints}
              onChange={(e) => setDefaultPoints(e.target.value)}
            />
          </Col>
        </Row>
        )}

        <Col marg="0 0 12px">
          {isJa ? (
            <Input
              title="Question template (日本語) — applied when rows are generated"
              placeholder={questionTemplate || 'デイリークイズ {{n}}'}
              value={questionTemplateJa}
              onChange={(e) => setQuestionTemplateJa(e.target.value)}
              wid="100%"
            />
          ) : (
            <Input
              title="Question template (use {{n}} for the row number)"
              value={questionTemplate}
              onChange={(e) => setQuestionTemplate(e.target.value)}
              wid="100%"
            />
          )}
        </Col>

        {!isJa && (
          <Row between center marg="0 0 12px">
            <Text>Default active</Text>
            <Switch activated={defaultActive} onClick={() => setDefaultActive((v) => !v)} />
          </Row>
        )}

        {!isJa && (
          <Row marg="0 0 16px">
            <Button
              text={`Generate ${effectiveCount} rows`}
              onClick={handleGenerate}
              col={theme.PRIMARY}
              style={{ width: 'fit-content' }}
            />
          </Row>
        )}

        <Text h4 marg="0 0 8px">
          {isJa ? '2. Default answer template — 日本語' : '2. Default answer template'}
        </Text>
        <Text c2 col={theme.GREY_60} marg="0 0 8px">
          Seeds the answers on every row when you generate. Each row keeps its own answers
          afterwards — expand a row below to edit them.
        </Text>

        {answerTemplate.map((a, i) => (
          <Row key={i} center marg="0 0 6px">
            {/* In the Japanese view the correct marker is read-only context. */}
            <Row
              centerAll
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: `1px solid ${a.isCorrect ? theme.GREEN : theme.GREY_40}`,
                backgroundColor: a.isCorrect ? theme.GREEN : 'transparent',
                cursor: isJa ? 'default' : 'pointer',
                marginRight: 8
              }}
              onClick={() => !isJa && updateTemplateAnswerCorrect(i)}
            >
              {a.isCorrect && <Icon type="check" col="white" size={12} />}
            </Row>
            <Col flex={1} marg="0 8px 0 0">
              <Input
                placeholder={isJa ? a.text || `回答 ${i + 1}` : `Answer ${i + 1}`}
                value={isJa ? a.textJa || '' : a.text}
                onChange={(e) =>
                  isJa
                    ? updateTemplateAnswerTextJa(i, e.target.value)
                    : updateTemplateAnswerText(i, e.target.value)
                }
                error={dupTemplateIdx.has(i)}
                wid="100%"
                noTitle
              />
            </Col>
            {!isJa && (
              <Icon
                type="close"
                col={answerTemplate.length > 2 ? theme.GREY_60 : theme.GREY_20}
                onClick={() => removeTemplateAnswer(i)}
                style={{ cursor: answerTemplate.length > 2 ? 'pointer' : 'not-allowed' }}
              />
            )}
          </Row>
        ))}
        {dupTemplateIdx.size > 0 && (
          <Text c2 col={theme.SECONDARY} marg="2px 0 8px">
            {isJa
              ? 'Duplicate Japanese options are not allowed. Please enter a unique option.'
              : 'Duplicate options are not allowed. Please enter a unique option.'}
          </Text>
        )}
        <Row marg="4px 0 16px" gap="8px">
          {!isJa && (
            <Button
              text="Add answer"
              onClick={addTemplateAnswer}
              bordered
              disabled={answerTemplate.length >= 10}
              style={{ width: 'fit-content', padding: '4px 12px', fontSize: 13 }}
            />
          )}
          {rows.length > 0 && (
            <Button
              text="Re-apply to all rows"
              onClick={reapplyTemplateToAll}
              bordered
              style={{ width: 'fit-content', padding: '4px 12px', fontSize: 13 }}
            />
          )}
        </Row>

        {rows.length > 0 && (
          <>
            <Text h4 marg="0 0 8px">
              3. Review &amp; edit ({rows.length})
            </Text>

            {(hasAnyConflict || topError) && (
              <Banner tone="error">
                {hasAnyConflict ? (
                  <div>
                    <strong>
                      {conflictCount} row{conflictCount === 1 ? '' : 's'} conflict
                      {conflictCount === 1 ? 's' : ''}
                    </strong>{' '}
                    with existing quizzes or each other. Affected rows are highlighted below.
                  </div>
                ) : (
                  <div>{topError}</div>
                )}
                {hasAnyConflict && (
                  <Row marg="10px 0 0" gap="8px" center>
                    <Button
                      text={shifting ? 'Recomputing…' : 'Auto-shift to skip conflicts'}
                      onClick={handleAutoShift}
                      bordered
                      disabled={shifting}
                      style={{ width: 'fit-content', padding: '4px 12px', fontSize: 13 }}
                    />
                    <Text c2 col={theme.GREY_60}>
                      Keeps the daily cadence, skips any day that overlaps another quiz.
                    </Text>
                  </Row>
                )}
              </Banner>
            )}

            {rows.map((r, i) => {
              const isExpanded = !!expanded[i];
              const correctIdx = r.answers.findIndex((a) => a.isCorrect);
              const filledCount = r.answers.filter((a) => a.text.trim().length > 0).length;
              const conflict = rowConflicts[i];
              const hasConflict = !!conflict;
              const dupRowIdx = duplicateIndices(r.answers, (a) => (isJa ? a.textJa : a.text));
              return (
                <RowWrap key={i} bad={hasConflict}>
                  <Row center>
                    <RowCell flex={0.4}>
                      <Text c2 col={theme.GREY_60}>
                        #{i + 1}
                      </Text>
                    </RowCell>
                    <RowCell flex={3}>
                      <Input
                        noTitle
                        placeholder={isJa ? r.question || '質問を入力してください' : undefined}
                        value={isJa ? r.questionJa || '' : r.question}
                        onChange={(e) =>
                          updateRow(
                            i,
                            isJa ? { questionJa: e.target.value } : { question: e.target.value }
                          )
                        }
                      />
                    </RowCell>
                    <RowCell flex={0.8}>
                      <Input
                        noTitle
                        type="number"
                        value={r.rewardPoints}
                        onChange={(e) => updateRow(i, { rewardPoints: e.target.value })}
                      />
                    </RowCell>
                    <RowCell flex={1.5}>
                      <DateInput
                        type="datetime-local"
                        value={r.startDate}
                        onChange={(e) => updateRow(i, { startDate: e.target.value })}
                        hasError={hasConflict}
                      />
                    </RowCell>
                    <RowCell flex={1.5}>
                      <DateInput
                        type="datetime-local"
                        value={r.endDate}
                        onChange={(e) => updateRow(i, { endDate: e.target.value })}
                        hasError={hasConflict}
                      />
                    </RowCell>
                    <RowCell flex={0.4}>
                      {!isJa && (
                        <Icon
                          type="close"
                          col={theme.GREY_60}
                          onClick={() => removeRow(i)}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                    </RowCell>
                  </Row>

                  {hasConflict && (
                    <Row marg="6px 0 0 12px">
                      <Text c2 col={theme.RED}>
                        {conflict.kind === 'existing'
                          ? `Overlaps "${conflict.quiz.question}"${
                              conflict.quiz.startDate && conflict.quiz.endDate
                                ? ` (${format(
                                    new Date(conflict.quiz.startDate),
                                    'MMM dd, h:mm a'
                                  )} → ${format(
                                    new Date(conflict.quiz.endDate),
                                    'MMM dd, h:mm a'
                                  )})`
                                : ''
                            }`
                          : `Overlaps row #${conflict.otherIndex + 1} in this batch`}
                      </Text>
                    </Row>
                  )}

                  <Row center marg="8px 0 0 12px" gap="10px">
                    <ExpandToggle onClick={() => toggleExpand(i)} type="button">
                      <Icon
                        type={isExpanded ? 'chevronDown' : 'chevronRight'}
                        size={12}
                        col={theme.GREY_60}
                      />
                      {filledCount} answer{filledCount === 1 ? '' : 's'}
                      {correctIdx >= 0 && r.answers[correctIdx].text && (
                        <Text c2 col={theme.GREEN} inline>
                          · correct: {r.answers[correctIdx].text}
                        </Text>
                      )}
                    </ExpandToggle>
                    {dupRowIdx.size > 0 && (
                      <Text c2 col={theme.SECONDARY} inline>
                        · duplicate answers
                      </Text>
                    )}
                  </Row>

                  {isExpanded && (
                    <Col marg="10px 0 4px 24px" pad="0 12px 0 0">
                      {r.answers.map((a, j) => (
                        <Row key={j} center marg="0 0 6px">
                          {/* In the Japanese view the correct marker is read-only context. */}
                          <Row
                            centerAll
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              border: `1px solid ${a.isCorrect ? theme.GREEN : theme.GREY_40}`,
                              backgroundColor: a.isCorrect ? theme.GREEN : 'transparent',
                              cursor: isJa ? 'default' : 'pointer',
                              marginRight: 8
                            }}
                            onClick={() => !isJa && updateRowAnswerCorrect(i, j)}
                          >
                            {a.isCorrect && <Icon type="check" col="white" size={12} />}
                          </Row>
                          <Col flex={1} marg="0 8px 0 0">
                            <Input
                              placeholder={isJa ? a.text || `回答 ${j + 1}` : `Answer ${j + 1}`}
                              value={isJa ? a.textJa || '' : a.text}
                              onChange={(e) =>
                                isJa
                                  ? updateRowAnswerTextJa(i, j, e.target.value)
                                  : updateRowAnswerText(i, j, e.target.value)
                              }
                              error={dupRowIdx.has(j)}
                              wid="100%"
                              noTitle
                            />
                          </Col>
                          {!isJa && (
                            <Icon
                              type="close"
                              col={r.answers.length > 2 ? theme.GREY_60 : theme.GREY_20}
                              onClick={() => removeRowAnswer(i, j)}
                              style={{ cursor: r.answers.length > 2 ? 'pointer' : 'not-allowed' }}
                            />
                          )}
                        </Row>
                      ))}
                      {dupRowIdx.size > 0 && (
                        <Text c2 col={theme.SECONDARY} marg="2px 0 4px">
                          {isJa
                            ? 'Duplicate Japanese options are not allowed. Please enter a unique option.'
                            : 'Duplicate options are not allowed. Please enter a unique option.'}
                        </Text>
                      )}
                      {!isJa && (
                        <Row marg="4px 0 0">
                          <Button
                            text="Add answer"
                            onClick={() => addRowAnswer(i)}
                            bordered
                            disabled={r.answers.length >= 10}
                            style={{ width: 'fit-content', padding: '4px 12px', fontSize: 13 }}
                          />
                        </Row>
                      )}
                    </Col>
                  )}
                </RowWrap>
              );
            })}
          </>
        )}

        <Row
          center
          justifyContent="flex-end"
          marg="24px 0 0"
          pad="16px 0 8px"
          style={{ borderTop: `1px solid ${theme.GREY_20}` }}
        >
          <Button
            text="Cancel"
            onClick={onClose}
            bordered
            marg="0 12px 0 0"
            style={{ width: 'fit-content', opacity: 0.8 }}
          />
          <Button
            text={
              submitting
                ? 'Submitting...'
                : hasAnyConflict
                  ? `${conflictCount} conflict${conflictCount === 1 ? '' : 's'} — resolve first`
                  : `Create ${rows.length || ''} quizzes`
            }
            onClick={handleSubmit}
            disabled={submitting || rows.length === 0 || hasAnyConflict}
            col={theme.PRIMARY}
            style={{
              width: 'fit-content',
              opacity: hasAnyConflict ? 0.6 : 1,
              cursor: hasAnyConflict ? 'not-allowed' : 'pointer'
            }}
          />
        </Row>
      </Col>

      <IrreversableModal
        visible={confirmReapplyOpen}
        heading="Re-apply template?"
        text="This replaces the answers on every generated row with the current template. Per-row edits will be lost."
        confirmText="Re-apply"
        onConfirm={doReapplyTemplate}
        closeModal={() => setConfirmReapplyOpen(false)}
        confirmNoClose
      />
    </Sidepanel>
  );
};

export default BulkCreateForm;
